import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { getHistory, deleteHistory } from '../utils/api';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import EmptyState from '../components/common/EmptyState';

const History = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const { confirm } = useConfirm();
  const { showToast } = useToast();

  const load = () => {
    setLoading(true);
    getHistory()
      .then(({ data }) => setRecords(data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!(await confirm({ title: 'Remove history record', message: 'This completion record will be permanently removed.' }))) return;
    try {
      await deleteHistory(id);
      setRecords((prev) => prev.filter((r) => r._id !== id));
      showToast('Record removed', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to remove record', 'error');
    }
  };

  // Group records by month
  const grouped = records.reduce((acc, r) => {
    const key = format(new Date(r.completedAt), 'MMMM yyyy');
    if (!acc[key]) acc[key] = [];
    acc[key].push(r);
    return acc;
  }, {});

  const totalCost = records.reduce((s, r) => s + (r.cost || 0), 0);

  if (loading) return <div className="loading">Loading…</div>;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Task History</h1>
          {records.length > 0 && (
            <p className="page-subtitle">{records.length} completions · ${totalCost.toFixed(2)} total cost</p>
          )}
        </div>
      </div>

      {records.length === 0 ? (
        <EmptyState
          icon="📋"
          title="No completed tasks yet"
          message="Every time you mark a task done, it's logged here with the date, cost, and notes."
          actionLabel="Go to Tasks"
          actionTo="/tasks"
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {Object.entries(grouped).map(([month, rows]) => {
            const monthCost = rows.reduce((s, r) => s + (r.cost || 0), 0);
            return (
              <div key={month} className="detail-section">
                <div className="section-header" style={{ marginBottom: '0.75rem' }}>
                  <h2>{month}</h2>
                  {monthCost > 0 && (
                    <span className="badge" style={{ background: '#e0e7ff', color: '#4f46e5', fontWeight: 700 }}>
                      ${monthCost.toFixed(2)}
                    </span>
                  )}
                </div>
                <div className="table-scroll">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Task</th>
                        <th>Category</th>
                        <th>Cost</th>
                        <th>Notes</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r._id}>
                          <td style={{ whiteSpace: 'nowrap' }}>{format(new Date(r.completedAt), 'MMM d')}</td>
                          <td>{r.task?.title || '—'}</td>
                          <td>
                            {r.task?.category
                              ? <span className={`badge badge-${r.task.category}`}>{r.task.category}</span>
                              : '—'}
                          </td>
                          <td>{r.cost != null && r.cost > 0 ? `$${Number(r.cost).toFixed(2)}` : '—'}</td>
                          <td style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>{r.notes || '—'}</td>
                          <td>
                            <button className="btn btn-danger btn-sm" onClick={() => handleDelete(r._id)}>Delete</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default History;
